import { useCallback, useEffect } from 'react';

import { useAtomValue } from 'jotai';

import { useCreateLayout, useUpdateActivity } from '@queries';
import { activeUserIdAtom, activityAtom, layoutAtom } from '@store';

import { useToggle } from './useToggle';

export const useLayoutEditing = () => {
    const layout = useAtomValue(layoutAtom);
    const activeUserId = useAtomValue(activeUserIdAtom);
    const activity = useAtomValue(activityAtom);

    const [isEditing, toggleIsEditing, setIsEditing] = useToggle(false);
    const [isEditingFloor, toggleIsEditingFloor, setIsEditingFloor] = useToggle(false);
    const [isEditingKeyParam, toggleIsEditingKeyParam, setIsEditingKeyParam] = useToggle(false);

    useEffect(() => {
        // we need to reset layout editing when layout changes
        setIsEditing(false);
        setIsEditingFloor(false);
        setIsEditingKeyParam(false);
    }, [layout?.id, setIsEditing, setIsEditingFloor]);

    const [createLayoutCopy, toggleCreateLayoutCopy] = useToggle(false);
    const { mutateAsync: copyLayout } = useCreateLayout();
    const { mutateAsync: updateActivity } = useUpdateActivity();

    const handleCreateLayoutCopy = useCallback(
        async (name: string) => {
            if (!layout) {
                throw new Error('No layout selected');
            }
            const newLayout = await copyLayout({ sourceLayoutId: layout!.id, name, userId: activeUserId });
            if (!newLayout) {
                throw new Error('Layout update failed');
            }

            if (activity) {
                await updateActivity({
                    layoutId: newLayout.id,
                    activityId: activity.id,
                });
            }

            toggleIsEditing();
            toggleCreateLayoutCopy();
        },
        [activeUserId, copyLayout, layout, toggleCreateLayoutCopy, toggleIsEditing],
    );

    const handleEditingLayout = useCallback(() => {
        if (isEditing || layout?.ownerUserId === activeUserId) {
            toggleIsEditing();
            return;
        }
        // when not layout owner and starting editing
        toggleCreateLayoutCopy();
    }, [activeUserId, isEditing, layout?.ownerUserId, toggleCreateLayoutCopy, toggleIsEditing]);

    const handleEditingKeyParam = useCallback(() => {
        if (isEditing || layout?.ownerUserId === activeUserId) {
            toggleIsEditingKeyParam();
            return;
        }
        // when not layout owner and starting editing
        toggleCreateLayoutCopy();
    }, [activeUserId, isEditing, layout?.ownerUserId, toggleCreateLayoutCopy, toggleIsEditing]);

    const handleEditingFloorLayout = useCallback(() => {
        if (isEditingFloor || layout?.ownerUserId === activeUserId) {
            toggleIsEditingFloor();
            return;
        }
        // when not layout owner and starting editing
        toggleCreateLayoutCopy();
    }, [activeUserId, isEditingFloor, layout?.ownerUserId, toggleCreateLayoutCopy, toggleIsEditingFloor]);

    const handleNavigateToEditor = useCallback(
        (nav: () => void) => {
            if (activeUserId !== layout?.ownerUserId) {
                toggleCreateLayoutCopy();
                return;
            }
            nav();
        },
        [activeUserId, layout?.ownerUserId, toggleCreateLayoutCopy],
    );

    return {
        isEditing,
        isEditingFloor,
        isEditingKeyParam,
        createLayoutCopy,
        toggleCreateLayoutCopy,
        handleCreateLayoutCopy,
        handleEditingLayout,
        handleEditingFloorLayout,
        handleEditingKeyParam,
        handleNavigateToEditor,
    };
};
